import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Calculator, Shield, FileText, DollarSign, TrendingUp, AlertTriangle, CheckCircle, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { ConversationalSidebar } from '../components/ConversationalSidebar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function InsurancePage() {
  const [activeTab, setActiveTab] = useState('calculator');
  const [loading, setLoading] = useState(false);
  
  // Quote Calculator State
  const [vesselName, setVesselName] = useState('');
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [coverageAmount, setCoverageAmount] = useState(1000000);
  const [windThreshold, setWindThreshold] = useState(40);
  const [delayThreshold, setDelayThreshold] = useState(5);
  const [quoteResult, setQuoteResult] = useState(null);
  const [savedQuotes, setSavedQuotes] = useState([]);
  
  // Risk Assessment State
  const [riskVesselName, setRiskVesselName] = useState('');
  const [riskOrigin, setRiskOrigin] = useState('');
  const [riskDestination, setRiskDestination] = useState('');
  const [vesselAge, setVesselAge] = useState(5);
  const [riskAssessment, setRiskAssessment] = useState(null);
  const [assessments, setAssessments] = useState([]);
  
  // Policy Management State
  const [policies, setPolicies] = useState([]);
  const [selectedPolicy, setSelectedPolicy] = useState(null);

  const ports = ['Singapore', 'Houston', 'Rotterdam', 'Shanghai', 'Mumbai'];

  useEffect(() => {
    fetchSavedQuotes();
    fetchAssessments();
    fetchPolicies();
  }, []);

  const fetchSavedQuotes = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/insurance/quotes`, {
        withCredentials: true
      });
      setSavedQuotes(response.data);
    } catch (error) {
      console.error('[Insurance] Error fetching quotes:', error);
    }
  };

  const fetchAssessments = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/insurance/risk/assessments`, {
        withCredentials: true
      });
      setAssessments(response.data);
    } catch (error) {
      console.error('[Insurance] Error fetching assessments:', error);
    }
  };

  const fetchPolicies = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/insurance/policies`, {
        withCredentials: true
      });
      setPolicies(response.data);
    } catch (error) {
      console.error('[Insurance] Error fetching policies:', error);
    }
  };

  const calculateQuote = async () => {
    if (!vesselName || !origin || !destination) {
      toast.error('Please fill all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/insurance/quotes/calculate`, null, {
        params: {
          vessel_name: vesselName,
          route: `${origin} → ${destination}`,
          origin,
          destination,
          coverage_amount: coverageAmount,
          wind_threshold: windThreshold,
          delay_threshold: delayThreshold
        },
        withCredentials: true
      });
      setQuoteResult(response.data);
      toast.success('Quote calculated successfully!');
    } catch (error) {
      console.error('[Insurance] Error calculating quote:', error);
      toast.error('Failed to calculate quote');
    } finally {
      setLoading(false);
    }
  };

  const saveQuote = async () => {
    if (!quoteResult) return;

    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/insurance/quotes/create`, null, {
        params: {
          vessel_name: vesselName,
          route: `${origin} → ${destination}`,
          origin,
          destination,
          coverage_amount: coverageAmount,
          wind_threshold: windThreshold,
          delay_threshold: delayThreshold
        },
        withCredentials: true
      });
      toast.success('Quote saved successfully!');
      await fetchSavedQuotes();
    } catch (error) {
      console.error('[Insurance] Error saving quote:', error);
      toast.error('Failed to save quote');
    } finally {
      setLoading(false);
    }
  };

  const assessRisk = async () => {
    if (!riskVesselName || !riskOrigin || !riskDestination) {
      toast.error('Please fill all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/insurance/risk/assess`, null, {
        params: {
          vessel_name: riskVesselName,
          route: `${riskOrigin} → ${riskDestination}`,
          origin: riskOrigin,
          destination: riskDestination,
          vessel_age: vesselAge
        },
        withCredentials: true
      });
      setRiskAssessment(response.data);
      await fetchAssessments();
      toast.success('Risk assessment complete!');
    } catch (error) {
      console.error('[Insurance] Error assessing risk:', error);
      toast.error('Failed to assess risk');
    } finally {
      setLoading(false);
    }
  };

  const createPolicy = async (quoteId) => {
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/insurance/policies/create`, null, {
        params: { quote_id: quoteId },
        withCredentials: true
      });
      toast.success('Policy created successfully!');
      await fetchPolicies();
      setActiveTab('policies');
    } catch (error) {
      console.error('[Insurance] Error creating policy:', error);
      toast.error(error.response?.data?.detail || 'Failed to create policy');
    } finally {
      setLoading(false);
    }
  };

  const simulateTrigger = async (policyId, triggerType) => {
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/insurance/policies/${policyId}/simulate-trigger`, null, {
        params: { trigger_type: triggerType },
        withCredentials: true
      });
      toast.success('Trigger simulated successfully!');
      await fetchPolicies();
    } catch (error) {
      console.error('[Insurance] Error simulating trigger:', error);
      toast.error('Failed to simulate trigger');
    } finally {
      setLoading(false);
    }
  };

  const getRiskScoreColor = (score) => {
    if (score >= 85) return 'text-[hsl(var(--success))]';
    if (score >= 70) return 'text-[hsl(var(--info))]';
    if (score >= 50) return 'text-[hsl(var(--warning))]';
    return 'text-[hsl(var(--destructive))]';
  };

  const getStatusBadgeColor = (status) => {
    switch(status) {
      case 'active': return 'bg-[hsl(var(--success))] text-white';
      case 'triggered': return 'bg-[hsl(var(--warning))] text-white';
      case 'expired': return 'bg-[hsl(var(--muted))] text-white';
      case 'cancelled': return 'bg-[hsl(var(--destructive))] text-white';
      default: return 'bg-[hsl(var(--secondary))]';
    }
  };

  return (
    <div className="space-y-8" data-testid="insurance-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'var(--font-display)' }}>
          Freight Insurance
        </h1>
        <p className="text-[hsl(var(--muted-foreground))] mt-1">
          Parametric quotes, risk assessment, and policy management
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="calculator" data-testid="tab-calculator">
            <Calculator className="h-4 w-4 mr-2" />
            Quote Calculator
          </TabsTrigger>
          <TabsTrigger value="risk" data-testid="tab-risk">
            <Shield className="h-4 w-4 mr-2" />
            Risk Assessment
          </TabsTrigger>
          <TabsTrigger value="policies" data-testid="tab-policies">
            <FileText className="h-4 w-4 mr-2" />
            My Policies
          </TabsTrigger>
        </TabsList>

        {/* Quote Calculator Tab */}
        <TabsContent value="calculator" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input Form */}
            <Card data-testid="quote-calculator-form">
              <CardHeader>
                <CardTitle>Coverage Parameters</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="vessel-name">Vessel Name *</Label>
                  <Input
                    id="vessel-name"
                    value={vesselName}
                    onChange={(e) => setVesselName(e.target.value)}
                    placeholder="e.g., Pacific Navigator"
                    data-testid="vessel-name-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="origin">Origin Port *</Label>
                    <Select value={origin} onValueChange={setOrigin}>
                      <SelectTrigger id="origin" data-testid="origin-select">
                        <SelectValue placeholder="Select port" />
                      </SelectTrigger>
                      <SelectContent>
                        {ports.map((port) => (
                          <SelectItem key={port} value={port}>{port}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="destination">Destination Port *</Label>
                    <Select value={destination} onValueChange={setDestination}>
                      <SelectTrigger id="destination" data-testid="destination-select">
                        <SelectValue placeholder="Select port" />
                      </SelectTrigger>
                      <SelectContent>
                        {ports.map((port) => (
                          <SelectItem key={port} value={port}>{port}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="coverage-amount">
                    Coverage Amount: ${(coverageAmount / 1000000).toFixed(1)}M
                  </Label>
                  <Slider
                    id="coverage-amount"
                    min={100000}
                    max={5000000}
                    step={100000}
                    value={[coverageAmount]}
                    onValueChange={(val) => setCoverageAmount(val[0])}
                    data-testid="coverage-slider"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="wind-threshold">
                    Wind Threshold: {windThreshold} knots
                  </Label>
                  <Slider
                    id="wind-threshold"
                    min={20}
                    max={60}
                    step={5}
                    value={[windThreshold]}
                    onValueChange={(val) => setWindThreshold(val[0])}
                    data-testid="wind-slider"
                  />
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    Payout triggers if wind exceeds this threshold
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="delay-threshold">
                    Delay Threshold: {delayThreshold} days
                  </Label>
                  <Slider
                    id="delay-threshold"
                    min={1}
                    max={14}
                    step={1}
                    value={[delayThreshold]}
                    onValueChange={(val) => setDelayThreshold(val[0])}
                    data-testid="delay-slider"
                  />
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    Payout triggers if voyage delayed beyond this
                  </p>
                </div>

                <Button
                  onClick={calculateQuote}
                  disabled={loading}
                  className="w-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                  data-testid="calculate-quote-button"
                >
                  <Calculator className="h-4 w-4 mr-2" />
                  {loading ? 'Calculating...' : 'Calculate Quote'}
                </Button>
              </CardContent>
            </Card>

            {/* Quote Result */}
            {quoteResult && (
              <Card data-testid="quote-result">
                <CardHeader>
                  <CardTitle>Your Quote</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="p-6 rounded-lg bg-[hsl(var(--secondary))]">
                    <div className="text-sm text-[hsl(var(--muted-foreground))] mb-2">Total Premium</div>
                    <div className="text-4xl font-semibold" style={{ fontFamily: 'var(--font-mono)' }}>
                      ${quoteResult.total_premium_usd.toLocaleString()}
                    </div>
                    <div className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
                      Coverage: ${(quoteResult.coverage_amount_usd / 1000000).toFixed(1)}M
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm">Premium Breakdown</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-[hsl(var(--muted-foreground))]">Base Premium</span>
                        <span style={{ fontFamily: 'var(--font-mono)' }}>
                          ${quoteResult.base_premium_usd.toLocaleString()}
                        </span>
                      </div>
                      {Object.entries(quoteResult.risk_factors).map(([key, value]) => (
                        <div key={key} className="flex justify-between text-sm">
                          <span className="text-[hsl(var(--muted-foreground))] capitalize">
                            {key.replace('_', ' ')} factor
                          </span>
                          <Badge variant="outline" className={value > 1 ? 'text-[hsl(var(--warning))]' : 'text-[hsl(var(--success))]'}>
                            ×{value}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm">Parametric Conditions</h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))]" />
                        <span>Wind exceeds {quoteResult.parametric_conditions.wind_threshold_knots} knots</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="h-4 w-4 text-[hsl(var(--info))]" />
                        <span>Delay exceeds {quoteResult.parametric_conditions.delay_threshold_days} days</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      onClick={saveQuote}
                      disabled={loading}
                      variant="outline"
                      data-testid="save-quote-button"
                    >
                      Save Quote
                    </Button>
                    <Button
                      onClick={() => {
                        saveQuote().then(() => {
                          // After saving, create policy with the saved quote
                          // In a real app, you'd get the quote ID from the response
                          toast.success('Quote saved! Create policy from saved quotes below.');
                        });
                      }}
                      disabled={loading}
                      className="bg-[hsl(var(--success))] text-white hover:bg-[hsl(var(--success))]/90"
                      data-testid="create-policy-button"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Save & Create Policy
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Saved Quotes */}
          {savedQuotes.length > 0 && (
            <Card data-testid="saved-quotes-card">
              <CardHeader>
                <CardTitle>Saved Quotes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {savedQuotes.slice(0, 5).map((quote) => (
                    <div key={quote.id} className="flex items-center justify-between p-4 rounded-lg border">
                      <div className="flex-1">
                        <div className="font-medium">{quote.vessel_name}</div>
                        <div className="text-sm text-[hsl(var(--muted-foreground))]">{quote.route}</div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                          Created {new Date(quote.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="text-right mr-4">
                        <div className="font-semibold" style={{ fontFamily: 'var(--font-mono)' }}>
                          ${quote.total_premium_usd.toLocaleString()}
                        </div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))]">
                          ${(quote.coverage_amount_usd / 1000000).toFixed(1)}M coverage
                        </div>
                      </div>
                      <Button
                        onClick={() => createPolicy(quote.id)}
                        disabled={loading}
                        size="sm"
                        data-testid={`create-policy-${quote.id}`}
                      >
                        Create Policy
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Risk Assessment Tab */}
        <TabsContent value="risk" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Risk Assessment Form */}
            <Card data-testid="risk-assessment-form">
              <CardHeader>
                <CardTitle>Risk Assessment Parameters</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="risk-vessel-name">Vessel Name *</Label>
                  <Input
                    id="risk-vessel-name"
                    value={riskVesselName}
                    onChange={(e) => setRiskVesselName(e.target.value)}
                    placeholder="e.g., Atlantic Star"
                    data-testid="risk-vessel-name-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="risk-origin">Origin Port *</Label>
                    <Select value={riskOrigin} onValueChange={setRiskOrigin}>
                      <SelectTrigger id="risk-origin" data-testid="risk-origin-select">
                        <SelectValue placeholder="Select port" />
                      </SelectTrigger>
                      <SelectContent>
                        {ports.map((port) => (
                          <SelectItem key={port} value={port}>{port}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="risk-destination">Destination Port *</Label>
                    <Select value={riskDestination} onValueChange={setRiskDestination}>
                      <SelectTrigger id="risk-destination" data-testid="risk-destination-select">
                        <SelectValue placeholder="Select port" />
                      </SelectTrigger>
                      <SelectContent>
                        {ports.map((port) => (
                          <SelectItem key={port} value={port}>{port}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vessel-age">
                    Vessel Age: {vesselAge} years
                  </Label>
                  <Slider
                    id="vessel-age"
                    min={0}
                    max={30}
                    step={1}
                    value={[vesselAge]}
                    onValueChange={(val) => setVesselAge(val[0])}
                    data-testid="vessel-age-slider"
                  />
                </div>

                <Button
                  onClick={assessRisk}
                  disabled={loading}
                  className="w-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                  data-testid="assess-risk-button"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  {loading ? 'Assessing...' : 'Assess Risk'}
                </Button>
              </CardContent>
            </Card>

            {/* Risk Assessment Result */}
            {riskAssessment && (
              <Card data-testid="risk-assessment-result">
                <CardHeader>
                  <CardTitle>Risk Assessment</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 rounded-lg bg-[hsl(var(--secondary))]">
                      <div className="text-sm text-[hsl(var(--muted-foreground))] mb-2">Vessel Reliability</div>
                      <div className={`text-3xl font-semibold ${getRiskScoreColor(riskAssessment.vessel_reliability_score)}`} style={{ fontFamily: 'var(--font-mono)' }}>
                        {riskAssessment.vessel_reliability_score}
                      </div>
                    </div>
                    <div className="text-center p-4 rounded-lg bg-[hsl(var(--secondary))]">
                      <div className="text-sm text-[hsl(var(--muted-foreground))] mb-2">Route Difficulty</div>
                      <div className={`text-3xl font-semibold ${getRiskScoreColor(riskAssessment.route_difficulty_score)}`} style={{ fontFamily: 'var(--font-mono)' }}>
                        {riskAssessment.route_difficulty_score}
                      </div>
                    </div>
                    <div className="text-center p-4 rounded-lg bg-[hsl(var(--secondary))]">
                      <div className="text-sm text-[hsl(var(--muted-foreground))] mb-2">Overall Risk</div>
                      <div className={`text-3xl font-semibold ${getRiskScoreColor(riskAssessment.overall_risk_score)}`} style={{ fontFamily: 'var(--font-mono)' }}>
                        {riskAssessment.overall_risk_score}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm">Risk Factors</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {Object.entries(riskAssessment.risk_factors).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-center p-3 rounded-lg bg-[hsl(var(--secondary))] text-sm">
                          <span className="text-[hsl(var(--muted-foreground))] capitalize">
                            {key.replace(/_/g, ' ')}
                          </span>
                          <span className="font-medium">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm">Recommendations</h4>
                    <ul className="space-y-2">
                      {riskAssessment.recommendations.map((rec, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-[hsl(var(--accent))] mt-0.5 flex-shrink-0" />
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Recent Assessments */}
          {assessments.length > 0 && (
            <Card data-testid="recent-assessments-card">
              <CardHeader>
                <CardTitle>Recent Assessments</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {assessments.slice(0, 5).map((assessment) => (
                    <div key={assessment.id} className="flex items-center justify-between p-4 rounded-lg border">
                      <div className="flex-1">
                        <div className="font-medium">{assessment.vessel_name}</div>
                        <div className="text-sm text-[hsl(var(--muted-foreground))]">{assessment.route}</div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                          Assessed {new Date(assessment.assessed_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <div className="text-xs text-[hsl(var(--muted-foreground))]">Vessel</div>
                          <div className={`text-lg font-semibold ${getRiskScoreColor(assessment.vessel_reliability_score)}`}>
                            {assessment.vessel_reliability_score}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-[hsl(var(--muted-foreground))]">Route</div>
                          <div className={`text-lg font-semibold ${getRiskScoreColor(assessment.route_difficulty_score)}`}>
                            {assessment.route_difficulty_score}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-[hsl(var(--muted-foreground))]">Overall</div>
                          <div className={`text-lg font-semibold ${getRiskScoreColor(assessment.overall_risk_score)}`}>
                            {assessment.overall_risk_score}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* My Policies Tab */}
        <TabsContent value="policies" className="space-y-6">
          {policies.length === 0 ? (
            <Card>
              <CardContent className="py-16">
                <div className="text-center space-y-4">
                  <FileText className="h-12 w-12 text-[hsl(var(--muted-foreground))] mx-auto" />
                  <h3 className="text-lg font-semibold">No Policies Yet</h3>
                  <p className="text-[hsl(var(--muted-foreground))] max-w-md mx-auto">
                    Create a quote and convert it to a policy to get started with freight insurance coverage.
                  </p>
                  <Button
                    onClick={() => setActiveTab('calculator')}
                    className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                  >
                    Calculate Quote
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {policies.map((policy) => (
                <Card key={policy.id} className="hover:shadow-md transition-shadow" data-testid={`policy-${policy.id}`}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{policy.vessel_name}</CardTitle>
                        <p className="text-sm text-[hsl(var(--muted-foreground))]" style={{ fontFamily: 'var(--font-mono)' }}>
                          {policy.policy_number}
                        </p>
                      </div>
                      <Badge className={getStatusBadgeColor(policy.status)} data-testid="policy-status-badge">
                        {policy.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-[hsl(var(--muted-foreground))]">Route</span>
                        <span className="font-medium">{policy.route}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[hsl(var(--muted-foreground))]">Coverage</span>
                        <span className="font-medium" style={{ fontFamily: 'var(--font-mono)' }}>
                          ${(policy.coverage_amount_usd / 1000000).toFixed(1)}M
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[hsl(var(--muted-foreground))]">Premium Paid</span>
                        <span className="font-medium" style={{ fontFamily: 'var(--font-mono)' }}>
                          ${policy.premium_paid_usd.toLocaleString()}
                        </span>
                      </div>
                    </div>

                    <div className="pt-3 border-t">
                      <h4 className="text-xs font-semibold mb-2">Parametric Triggers</h4>
                      <div className="space-y-2">
                        {policy.trigger_events.map((trigger, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs">
                            {trigger.triggered ? (
                              <CheckCircle className="h-3 w-3 text-[hsl(var(--warning))]" />
                            ) : (
                              <XCircle className="h-3 w-3 text-[hsl(var(--muted-foreground))]" />
                            )}
                            <span className={trigger.triggered ? 'text-[hsl(var(--warning))]' : ''}>
                              {trigger.description}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="pt-3 border-t text-xs text-[hsl(var(--muted-foreground))]">
                      <div>Valid: {new Date(policy.start_date).toLocaleDateString()} - {new Date(policy.end_date).toLocaleDateString()}</div>
                    </div>

                    {policy.status === 'active' && (
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm" className="w-full" data-testid="simulate-trigger-button">
                            Simulate Trigger (Test)
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Simulate Parametric Trigger</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <p className="text-sm text-[hsl(var(--muted-foreground))]">
                              Simulate a trigger event for testing purposes:
                            </p>
                            <div className="space-y-2">
                              <Button
                                onClick={() => simulateTrigger(policy.id, 'wind_exceeded')}
                                disabled={loading}
                                className="w-full"
                                data-testid="trigger-wind-button"
                              >
                                <AlertTriangle className="h-4 w-4 mr-2" />
                                Trigger Wind Event
                              </Button>
                              <Button
                                onClick={() => simulateTrigger(policy.id, 'delay_exceeded')}
                                disabled={loading}
                                className="w-full"
                                data-testid="trigger-delay-button"
                              >
                                <Clock className="h-4 w-4 mr-2" />
                                Trigger Delay Event
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* AI Conversational Sidebar */}
      <ConversationalSidebar 
        module="insurance"
        context={{
          quoteResult: quoteResult,
          savedQuotes: savedQuotes,
          riskAssessment: riskAssessment,
          assessments: assessments,
          policies: policies
        }}
        onAction={(type, params) => {
          console.log('[Insurance] AI Action:', type, params);
          // Actions can be implemented: calculate quote, assess risk, etc.
        }}
      />
    </div>
  );
}
